import { Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertService, DropdownModule, ModalComponent, ModalModule, StatusModule } from '@michelin/theme';
import { Store } from '@ngxs/store';
import { catchError, debounceTime, finalize, tap } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { DealerState } from '../../../../../shared/state/DealerState';
import { Dealer } from '../../../../../shared/dealer/models/dealer';
import { CreateWorkOrderService } from '../../../components/createworkorder/create-worker-order.service';
import { TranslationService } from '../../../../../shared/translations/services/translation.service';
import { CamRequest } from '../../../../asset-management/models/request/CamRequest';
import {
  ApplicationOption,
  AxleOption,
  CasingGradeOption,
  Plant,
  ProcessCode,
  ProductionCustomer,
  TireMake,
  TireSpecification,
  WheelOption,
  LengthOption,
  WorkOrderLine as WorkOrderLineInterface
} from '../../../components/createworkorder/models/work-order.interfaces';
import { WorkOrderSettings } from '../../../components/createworkorder/models/work-order-settings.model';
import { optionalBrandNumber, optionalNumber, optionalPattern, sanitizeBrandNumber } from '../../util/work-order-validators';
import { MichelinDropdownAccessibilityDirective } from '../../../../../shared/directives/michelin-dropdown-accessibility.directive';
import { MichelinDropdownOverlayStateService } from '../../../../../shared/directives/michelin-dropdown-overlay-state.service';

@Component({
  selector: 'app-wo-line-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    DropdownModule,
    MichelinDropdownAccessibilityDirective,
    ModalModule,
    StatusModule
  ],
  templateUrl: './wo-line-modal.component.html',
  styleUrl: './wo-line-modal.component.scss'
})
export class WoLineModalComponent implements OnInit {
  @ViewChild('modalRef') modalRef!: ModalComponent;
  @ViewChild('modalContent') modalContent!: TemplateRef<any>;

  // -------- Inputs --------
  @Input() plant: Plant | null = null;
  @Input() selectedCustomerUuid = '';
  @Input() customerRtDot = '';
  @Input() customerTreadDepth = '';
  @Input() customerDisposalChargesFk = '';
  @Input() headerSettings: WorkOrderSettings | null = null;
  @Input() existingLines: WorkOrderLineInterface[] = [];
  @Input() productionCustomers: ProductionCustomer[] = [];
  @Input() preferredTreadSpecPk: string | null = null;
  @Input() addingLineToExistingWo = false;
  @Input() currentLineNo: number;
  @Input() rememberBrand = '';
  @Input() rememberCasingNote = '';

  // -------- Outputs --------
  @Output() lineAdded = new EventEmitter<WorkOrderLineInterface>();
  @Output() lineUpdated = new EventEmitter<WorkOrderLineInterface>();

  // -------- Services --------
  private readonly store = inject(Store);
  readonly alertService = inject(AlertService);
  readonly createWorkOrderService = inject(CreateWorkOrderService);
  public readonly translationService = inject(TranslationService);
  private readonly elementRef = inject(ElementRef);
  private readonly michelinDropdownOverlayState = inject(MichelinDropdownOverlayStateService);

  // -------- Dealer --------
  private dealer!: Dealer;

  // Expose Validators to template for hasValidator checks
  readonly validators = Validators;

  // -------- Modal state --------
  isEditMode = false;
  editingLine: WorkOrderLineInterface | null = null;
  submittedLine = false;
  lineErrors: string[] = [];

  // -------- Visual flags --------
  showMoldRadio = false;
  showTreadDepthFields = false;
  showCasingValueField = false;
  barcodeRequired = false;
  // Whether tread selection is currently required (kept in sync by updateTreadRequiredBasedOnMold)
  treadRequired = false;

  // -------- SC/NP process code flags --------
  lineScProcessCode = false;
  lineNpProcessCode = false;

  // -------- CAM validation --------
  /** Structured pending violation awaiting user action (Fix / Ignore / Reject / Downgrade). Blocks save until resolved. */
  pendingCamViolation: {
    type: 'ntr' | 'age';
    value: string;
    limit: string;
    action: string;
    disposition: string;
    treads?: string[];
    application?: string | null;
    axle?: string | null;
  } | null = null;
  /** True when user chose Reject for a CAM violation — carried on the emitted WorkOrderLine. */
  lineCamRejected = false;
  /** CAM violation field that triggered the reject ('NTR' or 'AGE') — sent to backend. */
  lineCamViolationField: string | null = null;
  /** Disposition from the violated CAM spec — sent to backend. */
  lineCamViolationDisposition: string | null = null;
  /** @deprecated use pendingCamViolation — kept for template backward compat during transition */
  get lineCamNtrError(): string | null {
    return this.pendingCamViolation?.type === 'ntr'
      ? `NTR value ${this.pendingCamViolation.value} exceeds the CAM Spec limit of ${this.pendingCamViolation.limit}`
      : null;
  }
  /** @deprecated use pendingCamViolation — kept for template backward compat during transition */
  get lineCamMdotError(): string | null {
    return this.pendingCamViolation?.type === 'age'
      ? `MDOT age ${this.pendingCamViolation.value} months exceeds the CAM Spec limit of ${this.pendingCamViolation.limit} months`
      : null;
  }
  lineNtrCamValidating = false;
  lineMdotCamValidating = false;
  /** Tread options available for DOWNGRADE — intersection of authorized treads and CAM spec downgrade list. */
  camDowngradeTreads: TireSpecification[] = [];
  /** The tread selected by the user in the downgrade dropdown. */
  selectedDowngradeTread: TireSpecification | null = null;
  /** Accumulates all violation types dismissed via Fix — each is re-validated on the next save attempt. */
  private camFixedViolationTypes = new Set<'ntr' | 'age'>();
  /** Set by recheckCamFields when both NTR and AGE were Fixed — chains NTR→AGE check before saving. */
  private camRecheckPendingAge = false;
  /** Deferred save action waiting for CAM re-validation to complete cleanly. */
  private pendingSaveAction: (() => void) | null = null;

  // -------- Barcode validation --------
  barcodeValidationLoading = false;

  // -------- RT DOT validation --------
  rtDotValidating = false;

  // -------- Dropdowns --------
  lineProcessCodes: ProcessCode[] = [];
  makes: TireMake[] = [];
  series: TireSpecification[] = [];
  sizes: TireSpecification[] = [];
  treads: TireSpecification[] = [];
  lengthOptions: LengthOption[] = [];
  applicationOptions: ApplicationOption[] = [];
  axleOptions: AxleOption[] = [];
  wheelOptions: WheelOption[] = [];
  wheelColorOptions: WheelOption[] = [];
  casingGradeOptions: CasingGradeOption[] = [];
  rejectReasonOptions: any[] = [];

  // -------- Loading flags --------
  lineProcessCodesLoading = false;
  makesLoading = false;
  seriesLoading = false;
  sizesLoading = false;
  treadsLoading = false;

  // -------- Session line tracking --------
  // Tracks lines added in the current open session (before Angular CD propagates them back
  // into existingLines). Used by getNextLineNumber() to produce correct sequential numbers
  // when the user adds multiple lines via "Add and stay open" without closing the modal.
  private sessionLines: WorkOrderLineInterface[] = [];

  // -------- Debounce subjects --------
  private readonly ntrChangeSubject = new Subject<string>();
  private readonly mfgDotChangeSubject = new Subject<string>();
  private readonly barcodeChangeSubject = new Subject<string>();
  private readonly rtDotChangeSubject = new Subject<string>();

  // -------- Form --------
  lineWOForm: FormGroup = new FormGroup(
    {
      barcode: new FormControl('', [Validators.maxLength(10), optionalPattern(/^[A-Za-z0-9]+$/, 'barcode')]),
      casingLocation: new FormControl(''),
      casingStatus: new FormControl(''),
      workType: new FormControl(''),
      post: new FormControl(''),
      woSite: new FormControl(''),
      processCodeId: new FormControl('', [Validators.required]),
      productionCustomerId: new FormControl('', [Validators.required]),
      customerCode: new FormControl(''),
      customerName: new FormControl(''),
      receivedDate: new FormControl(''),
      completeDate: new FormControl(''),
      dueDate: new FormControl(''),
      makeId: new FormControl('', [Validators.required]),
      seriesId: new FormControl('', [Validators.required]),
      sizeId: new FormControl('', [Validators.required]),
      treadId: new FormControl(''),
      moldType: new FormControl('', Validators.maxLength(30)),
      application: new FormControl(''),
      axle: new FormControl(''),
      treadWidth: new FormControl(''),
      speedIndex: new FormControl(''),
      ntrId: new FormControl('', [Validators.maxLength(1), optionalPattern(/^[0-9]{1}$/, 'ntrDigit')]),
      ignoreCheckCamSpecNtr: new FormControl(''),
      ignoreCheckCamSpecAge: new FormControl(''),
      mfgDot: new FormControl('', [Validators.maxLength(4), optionalPattern(/^[A-Za-z0-9]*$/, 'alnum')]),
      rtDot: new FormControl('', [Validators.maxLength(7), optionalPattern(/^[A-Za-z0-9]*$/, 'alnum')]),
      brandNumber: new FormControl('', [Validators.maxLength(25), optionalBrandNumber()]),
      treadDepthMin: new FormControl('', [Validators.maxLength(2), optionalPattern(/^[0-9]*$/, 'num')]),
      treadDepthMax: new FormControl('', [Validators.maxLength(2), optionalPattern(/^[0-9]*$/, 'num')]),
      lengthId: new FormControl(''),
      casingValue: new FormControl(''),
      scrapCode: new FormControl(''),
      npCode: new FormControl(''),
      disposalCharge: new FormControl(''),
      sequenceNumber: new FormControl(''),
      repair: new FormControl(''),
      chamberNum: new FormControl(''),
      minCircumference: new FormControl(''),
      maxCircumference: new FormControl(''),
      casingNotes: new FormControl('', [Validators.maxLength(70)]),
      camSpec: new FormControl(''),
      systemGrade: new FormControl(''),
      overrideGrade: new FormControl(''),
      wheelType: new FormControl(''),
      wheelColor: new FormControl(''),
      sendToServicePostFlag: new FormControl('N')
    },
    { validators: [this.lineGroupValidator()] }
  );

  // -------- Lifecycle --------

  onBrandNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = sanitizeBrandNumber(input.value);
    input.value = value;
    this.lineWOForm.get('brandNumber')?.setValue(value, { emitEvent: false });
  }

  ngOnInit(): void {
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.loadStaticDropdowns();
    this.setupFormValueChanges();
    this.setupDebouncedValidators();
  }

  // -------- Public API --------

  /** Open in add mode. */
  open(): void {
    this.isEditMode = false;
    this.editingLine = null;
    this.submittedLine = false;
    this.lineErrors = [];
    this.pendingCamViolation = null;
    this.lineCamRejected = false;
    this.lineCamViolationField = null;
    this.lineCamViolationDisposition = null;
    this.camDowngradeTreads = [];
    this.selectedDowngradeTread = null;
    this.sessionLines = [];
    this.applyPlantSettings();
    this.resetLineForm();
    this.applyCustomerSettings();
    this.loadLineProcessCodes(this.selectedCustomerUuid).subscribe();
    this.autoSelectMatchingProductionCustomer();
    const addDialogRef = this.modalRef.open({ id: 'add-wo-line', width: 1150, title: '', content: this.modalContent });
    addDialogRef.disableClose = true;
    addDialogRef.keydownEvents().subscribe((e) => {
      // Ignore Escape while a Michelin dropdown panel is open — that keypress is meant to
      // close the dropdown only (Michelin/Material don't stop it from bubbling up to us).
      if (e.key === 'Escape' && !this.hasOpenMichelinDropdownPanel()) addDialogRef.close();
    });
  }

  /** Open in edit mode for a single line. */
  openForEdit(item: WorkOrderLineInterface): void {
    this.isEditMode = true;
    this.editingLine = item;
    this.submittedLine = false;
    this.lineErrors = [];
    this.sessionLines = [];
    this.resetLineForm();
    // Restore line-specific CAM state AFTER resetLineForm() so it isn't wiped.
    this.lineCamRejected = item.rejectInd === 'Y';
    this.lineCamViolationField = item.camViolationField ?? null;
    this.lineCamViolationDisposition = item.camViolationDisposition ?? null;
    this.applyPlantSettings();
    this.applyCustomerSettings();
    this.loadLineProcessCodes(this.selectedCustomerUuid).subscribe(() => {
      this.patchFormForEdit(item);
    });
    const editDialogRef = this.modalRef.open({ id: 'edit-wo-line', width: 1200, title: '', content: this.modalContent });
    editDialogRef.disableClose = true;
    editDialogRef.keydownEvents().subscribe((e) => {
      // Ignore Escape while a Michelin dropdown panel is open — that keypress is meant to
      // close the dropdown only (Michelin/Material don't stop it from bubbling up to us).
      if (e.key === 'Escape' && !this.hasOpenMichelinDropdownPanel()) editDialogRef.close();
    });
  }

  /**
   * Checks LIVE DOM state (rather than a cached flag) for whether a Michelin/Material select
   * panel is currently open anywhere. CDK overlay panes render as siblings under a global overlay
   * container, not nested inside this modal's DOM, so keydown propagation/dispatch order between
   * the panel and the dialog isn't reliable — a direct DOM check is the only deterministic signal.
   */
  private hasOpenMichelinDropdownPanel(): boolean {
    return !!this.michelinDropdownOverlayState.hasOpenDropdown || !!document.querySelector('.mat-mdc-select-panel');
  }

  // -------- Getters --------

  get isSAZone(): boolean {
    return this.headerSettings?.zone?.toUpperCase() === 'SA';
  }

  getNextLineNumber(): number {
    // Count session lines that haven't been reflected back into existingLines via Angular CD yet.
    // This prevents duplicate line numbers when the user adds multiple lines via "Add and stay open"
    // before the @Input() existingLines binding is updated.
    const pendingCount = this.sessionLines.filter((sl) => !this.existingLines.some((el) => el.id === sl.id)).length;
    return this.existingLines.length + pendingCount + 1;
  }

  compareById = (a: any, b: any) => a && b && a.id === b.id;

  get isLineAddDisabled(): boolean {
    return (
      (!this.lineCamRejected && !this.lineWOForm.valid) ||
      this.lineNtrCamValidating ||
      this.lineMdotCamValidating ||
      this.barcodeValidationLoading ||
      this.rtDotValidating ||
      !!this.pendingCamViolation
    );
  }

  // -------- Modal actions --------

  /** Add another line (stays open). */
  addWorkOrderLine(): void {
    this.submittedLine = true;
    this.lineWOForm.markAllAsTouched();
    // Pending violation must be resolved (Fix / Ignore / Reject) before saving
    if (this.pendingCamViolation) return;
    if (!this.lineCamRejected && !this.lineWOForm.valid) return;
    // If the user previously clicked Fix, re-validate CAM fields before committing the save.
    // This catches the case where the user dismissed the banner without actually correcting the value.
    if (this.camFixedViolationTypes.size > 0) {
      this.pendingSaveAction = () => this.doAddWorkOrderLine();
      this.recheckCamFields();
      return;
    }
    this.doAddWorkOrderLine();
  }

  private doAddWorkOrderLine(): void {
    const newLine = this.createWorkOrderLineFromFormData();
    if (this.isEditMode) {
      this.lineUpdated.emit(newLine);
    } else {
      this.lineAdded.emit(newLine);
      // Track lines added in this session so getNextLineNumber() stays accurate even
      // before Angular CD propagates the updated existingLines @Input back to this component.
      this.sessionLines.push(newLine);
    }
    this.resetForNextLine();
    this.submittedLine = false;
    this.pendingCamViolation = null;
    this.lineCamRejected = false;
    this.lineCamViolationField = null;
    this.lineCamViolationDisposition = null;
    this.camDowngradeTreads = [];
    this.selectedDowngradeTread = null;
  }

  /** Add line and close. */
  onAddLineAndClose(): void {
    this.submittedLine = true;
    this.lineWOForm.markAllAsTouched();
    // Pending violation must be resolved (Fix / Ignore / Reject) before saving
    if (this.pendingCamViolation) return;
    if (!this.lineCamRejected && !this.lineWOForm.valid) return;
    // If the user previously clicked Fix, re-validate CAM fields before committing the save.
    if (this.camFixedViolationTypes.size > 0) {
      this.pendingSaveAction = () => this.doAddLineAndClose();
      this.recheckCamFields();
      return;
    }
    this.doAddLineAndClose();
  }

  onMoldTypeEnterKey(value: 'P' | 'M', event: Event): void {
    event.preventDefault();
    this.lineWOForm.patchValue({ moldType: value });
    this.lineWOForm.get('moldType')?.markAsTouched();
  }

  private doAddLineAndClose(): void {
    const newLine = this.createWorkOrderLineFromFormData();
    if (this.isEditMode) {
      this.lineUpdated.emit(newLine);
    } else {
      this.lineAdded.emit(newLine);
    }
    this.modalRef.dialog?.closeAll();
  }

  // -------- CAM violation actions --------

  /** Fix: dismiss banner so user can correct the field value. lineCamRejected is intentionally NOT cleared here —
   * a prior reject on a different field (e.g. NTR rejected, then MDOT appears) must survive. */
  onCamFix(): void {
    if (this.pendingCamViolation) {
      this.camFixedViolationTypes.add(this.pendingCamViolation.type);
    }
    this.pendingCamViolation = null;
    this.camDowngradeTreads = [];
    this.selectedDowngradeTread = null;
  }

  /** Ignore: override the CAM rule — accept the entered value and continue. lineCamRejected is intentionally NOT cleared
   * here — a prior reject on a different field (e.g. NTR rejected, then MDOT ignored) must survive. */
  onCamIgnore(): void {
    if (!this.pendingCamViolation) return;
    const violationType = this.pendingCamViolation.type;
    const ignoreShopFloor = this.plant?.cKCamSpecWoShopFloor?.toUpperCase() === 'Y';
    const ignoreConstant = ignoreShopFloor ? 'Y' : 'I';
    if (violationType === 'ntr') {
      this.lineWOForm.patchValue({ ignoreCheckCamSpecNtr: ignoreConstant }, { emitEvent: false });
    } else {
      this.lineWOForm.patchValue({ ignoreCheckCamSpecAge: ignoreConstant }, { emitEvent: false });
    }
    this.pendingCamViolation = null;
    this.camDowngradeTreads = [];
    this.selectedDowngradeTread = null;
    // Ignore is a definitive resolution for this field — remove only it from pending rechecks.
    // Other fields still in the Set (e.g. AGE when NTR was ignored) will be rechecked on the next save.
    this.camFixedViolationTypes.delete(violationType);
    this.camRecheckPendingAge = false;
    this.pendingSaveAction = null;
    this.gradeAndWorktypeUpdate();
  }

  /** Reject: mark this line as CAM-rejected. Captures violation type and disposition for backend. */
  onCamReject(): void {
    if (!this.pendingCamViolation) return;
    const violation = this.pendingCamViolation;
    this.lineCamRejected = true;
    this.lineCamViolationField = violation.type === 'ntr' ? 'NTR' : 'AGE';
    this.lineCamViolationDisposition = violation.disposition || null;
    this.checkRtDotRequiredForReject();
    this.pendingCamViolation = null;
    this.camDowngradeTreads = [];
    this.selectedDowngradeTread = null;
    // Reject is a definitive resolution for this field — remove only it from pending rechecks.
    // Other fields still in the Set will be rechecked on the next save attempt.
    this.camFixedViolationTypes.delete(violation.type);
    this.camRecheckPendingAge = false;
    this.pendingSaveAction = null;
    // Fetch disposal charge if applicable
    if (violation.disposition && this.dealer?.id) {
      this.createWorkOrderService.getDisposalChargeByDescription(this.dealer.id, violation.disposition).subscribe({
        next: (dc) => this.lineWOForm.patchValue({ disposalCharge: dc ?? null }, { emitEvent: false }),
        error: () => this.lineWOForm.patchValue({ disposalCharge: null }, { emitEvent: false })
      });
    }
  }

  /**
   * Downgrade: replace the current tread with the CAM-spec recommended downgrade tread and re-run the
   * applicable CAM check. Mirrors legacy LimitFailureManagedBean.downGrade() which calls
   * PostProcess.downgrade() then checkCamNTRForLimitFailure / checkCamMDotForLimitFailure.
   */
  onCamDowngrade(): void {
    if (!this.selectedDowngradeTread || !this.pendingCamViolation) return;
    const violationType = this.pendingCamViolation.type;
    const violation = this.pendingCamViolation;
    this.lineWOForm.patchValue({ treadId: this.selectedDowngradeTread }, { emitEvent: false });
    // Set application and axle from CAM violation spec if provided — mirrors legacy downGrade()
    if (violation.application) {
      const matchedApp = this.applicationOptions.find(
        (opt) =>
          opt.value?.toLowerCase() === violation.application?.toLowerCase() ||
          opt.id?.toLowerCase() === violation.application?.toLowerCase()
      );
      if (matchedApp) {
        this.lineWOForm.patchValue({ application: matchedApp }, { emitEvent: false });
      }
    }
    if (violation.axle) {
      const matchedAxle = this.axleOptions.find(
        (opt) => opt.value?.toLowerCase() === violation.axle?.toLowerCase() || opt.id?.toLowerCase() === violation.axle?.toLowerCase()
      );
      if (matchedAxle) {
        this.lineWOForm.patchValue({ axle: matchedAxle }, { emitEvent: false });
      }
    }
    this.pendingCamViolation = null;
    this.camDowngradeTreads = [];
    this.selectedDowngradeTread = null;
    // Downgrade is a definitive resolution for this field — remove only it from pending rechecks.
    // Other fields still in the Set will be rechecked on the next save attempt.
    this.camFixedViolationTypes.delete(violationType);
    this.camRecheckPendingAge = false;
    this.pendingSaveAction = null;
    // Re-check the CAM spec with the new tread, mirroring the legacy re-check after downgrade
    if (violationType === 'ntr') {
      this.checkNtrCamViolation(this.lineWOForm.get('ntrId')?.value || '');
    } else {
      this.checkMfgDotCamViolation(this.lineWOForm.get('mfgDot')?.value || '');
    }
  }

  onCancelLine(): void {
    this.isEditMode = false;
    this.editingLine = null;
    this.modalRef.dialog?.closeAll();
  }

  onModalClosed(): void {
    this.isEditMode = false;
    this.editingLine = null;
  }

  formatCasingValue(): void {
    const ctrl = this.lineWOForm.get('casingValue');
    if (!ctrl || !this.showCasingValueField) return;
    const formatted = this.applyFormatNumber(String(ctrl.value ?? ''));
    ctrl.patchValue(formatted, { emitEvent: false });
    ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  onBarcodeBlur(): void {
    const val = (this.lineWOForm.get('barcode')?.value || '') as string;
    this.barcodeChangeSubject.next(val);
  }

  // -------- Private helpers --------

  // -------- CAM re-validation helpers --------

  /**
   * Re-validates every CAM field dismissed via "Fix" before allowing the save to proceed.
   * Uses `camFixedViolationTypes` (a Set) so multiple Fix clicks are all honoured — only the
   * dismissed fields are re-checked, preserving Ignore/Reject state on untouched fields.
   * When both NTR and AGE were fixed, NTR is checked first; if it passes `camRecheckPendingAge`
   * signals `checkNtrCamViolation` to chain straight into the AGE check before saving.
   */
  private recheckCamFields(): void {
    const checkCam = this.plant?.ckCamSpecWo === 'Y';
    const fixNtr = this.camFixedViolationTypes.has('ntr');
    const fixAge = this.camFixedViolationTypes.has('age');
    // Do NOT clear the Set here — entries are removed individually as each check passes.
    // This ensures that if one check fails and forces user interaction, the other field's
    // Fix record survives and is still rechecked on the next save attempt.

    if (fixNtr) {
      const ntrValue = this.lineWOForm.get('ntrId')?.value || '';
      const ntrNum = parseInt(ntrValue, 10);
      if (checkCam && !!ntrValue && !isNaN(ntrNum) && ntrNum > 0) {
        // If AGE was also Fixed, chain to AGE recheck after NTR passes.
        this.camRecheckPendingAge = fixAge && checkCam && !!(this.lineWOForm.get('mfgDot')?.value || '').trim();
        this.checkNtrCamViolation(ntrValue);
        return;
      }
      // NTR value cleared/empty — treat as passed and remove from pending rechecks.
      this.camFixedViolationTypes.delete('ntr');
    }

    if (fixAge) {
      const mfgDotValue = (this.lineWOForm.get('mfgDot')?.value || '').trim();
      if (checkCam && !!mfgDotValue) {
        this.camRecheckPendingAge = false;
        this.checkMfgDotCamViolation(mfgDotValue);
        return;
      }
      // AGE value cleared/empty — treat as passed and remove from pending rechecks.
      this.camFixedViolationTypes.delete('age');
    }

    this.executePendingSaveIfReady();
  }

  /** Runs the deferred save callback once all CAM re-checks have passed cleanly. */
  private executePendingSaveIfReady(): void {
    if (!this.pendingSaveAction || this.pendingCamViolation) return;
    const action = this.pendingSaveAction;
    this.pendingSaveAction = null;
    action();
  }

  private focusCamViolationBanner(): void {
    setTimeout(() => {
      const banner = this.elementRef.nativeElement.querySelector('.cam-violation-banner') as HTMLElement;
      if (!banner) return;
      if (this.camDowngradeTreads.length > 0) {
        const select = banner.querySelector<HTMLElement>('.cam-downgrade-section mat-select');
        select?.focus();
      } else if (this.headerSettings?.canIgnoreFleetSpecs) {
        const buttons = banner.querySelectorAll<HTMLElement>('.cam-violation-actions button');
        const ignoreBtn = Array.from(buttons).find((b) => b.textContent?.trim() === 'Ignore');
        ignoreBtn?.focus();
      } else {
        const fixBtn = banner.querySelector<HTMLElement>('.cam-violation-actions button');
        fixBtn?.focus();
      }
    }, 150);
  }

  private applyPlantSettings(): void {
    this.showMoldRadio = this.plant?.isHybridPlant === true;
    this.barcodeRequired = this.plant?.reqBarcodeEntry?.toUpperCase() !== 'N';
    this.updateBarcodeValidation();
    this.updateMoldRadioControl();
    this.updateMfgDotControl();
  }

  private applyCustomerSettings(): void {
    this.updateTreadDepthValidators();
    this.updateRtDotValidators();
    //this.updateLengthValidators();
  }

  private updateBarcodeValidation(): void {
    const ctrl = this.lineWOForm.get('barcode');
    if (!ctrl) return;
    if (this.barcodeRequired) {
      ctrl.setValidators([Validators.required, Validators.maxLength(10), optionalPattern(/^[A-Za-z0-9]+$/, 'barcode')]);
    } else {
      ctrl.setValidators([Validators.maxLength(10), optionalPattern(/^[A-Za-z0-9]+$/, 'barcode')]);
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private updateTreadDepthValidators(): void {
    const customerTreadFlag = (this.customerTreadDepth || '').toUpperCase();
    let treadDepthFlag = 'N';
    if (customerTreadFlag === 'R') {
      treadDepthFlag = this.plant?.reqTdInitial === 'Y' ? 'Y' : 'R';
    }

    this.showTreadDepthFields = treadDepthFlag === 'Y' || treadDepthFlag === 'R';
    const minCtrl = this.lineWOForm.get('treadDepthMin');
    const maxCtrl = this.lineWOForm.get('treadDepthMax');
    const baseTdValidators = [Validators.maxLength(2), optionalPattern(/^[0-9]*$/, 'num')];

    if (this.showTreadDepthFields) {
      const tdValidators = treadDepthFlag === 'R' ? [Validators.required, ...baseTdValidators] : baseTdValidators;
      minCtrl?.setValidators(tdValidators);
      maxCtrl?.setValidators(tdValidators);
      try {
        minCtrl?.enable({ emitEvent: false });
      } catch {
        console.log('Error enabling treadDepthMin control');
      }
      try {
        maxCtrl?.enable({ emitEvent: false });
      } catch {
        console.log('Error enabling treadDepthMax control');
      }
    } else {
      minCtrl?.clearValidators();
      maxCtrl?.clearValidators();
      try {
        minCtrl?.disable({ emitEvent: false });
      } catch {
        console.log('Error disabling treadDepthMin control');
      }
      try {
        maxCtrl?.disable({ emitEvent: false });
      } catch {
        console.log('Error disabling treadDepthMax control');
      }
      minCtrl?.patchValue('', { emitEvent: false });
      maxCtrl?.patchValue('', { emitEvent: false });
    }
    minCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    maxCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private updateMoldRadioControl(): void {
    const moldCtrl = this.lineWOForm.get('moldType');
    if (!moldCtrl) return;
    if (this.showMoldRadio) {
      if (moldCtrl.disabled) moldCtrl.enable({ emitEvent: false });
    } else {
      // Non-hybrid plant: always default to 'P' (Pre-Mold) — mirrors legacy isShowMoldRadio()
      // which calls wol.setMoldType(GraConstants.PRE_MOLD_CURE_TYPE) when not hybrid.
      moldCtrl.setValue('P', { emitEvent: false });
      moldCtrl.disable({ emitEvent: false });
    }
    this.updateMoldTypeRequired();
    this.updateTreadRequiredBasedOnMold();
  }

  /**
   * Mirrors legacy commonsValidator rendered condition on idPreMoldCustomMold:
   *   rendered="#{(workOrderDetail.treadDesignFlag && workOrderDetail.workTypeRR)
   *              || (workOrderDetail.workTypeAD && workOrderDetail.treadDescFilled)}"
   *
   * treadDesignFlag = (treadDescFlag == 'R') && processCode != AD && processCode != RS
   * moldType is required when the radio is shown AND the above condition holds.
   */
  private isMoldTypeRequired(): boolean {
    if (!this.showMoldRadio) return false;
    const pcVal = this.lineWOForm.get('processCodeId')?.value;
    const code = typeof pcVal === 'object' ? pcVal?.code || pcVal?.value || '' : pcVal || '';
    const upper = String(code).toUpperCase();
    const workType = this.lineWOForm.get('workType')?.value || '';
    const treadId = this.lineWOForm.get('treadId')?.value;
    const treadDescFlag = this.headerSettings?.treadDescFlag || '';

    // treadDesignFlag: RETREAD_TREAD == 'R' AND processCode is not AD or RS (for new lines, casingStatus is empty)
    const treadDesignFlag = treadDescFlag === 'R' && upper !== 'AD' && upper !== 'RS';
    const workTypeRR = workType === 'RR';
    const workTypeAD = workType === 'AD';
    const treadDescFilled = treadId !== null && treadId !== undefined && treadId !== '';
    // Exact mirror of legacy commonsValidator rendered condition:
    //   rendered="#{(workOrderDetail.treadDesignFlag && workOrderDetail.workTypeRR)
    //              || (workOrderDetail.workTypeAD && workOrderDetail.treadDescFilled)}"
    return (treadDesignFlag && workTypeRR) || (workTypeAD && treadDescFilled);
  }

  private updateMoldTypeRequired(): void {
    const moldCtrl = this.lineWOForm.get('moldType');
    if (!moldCtrl) return;
    if (this.isMoldTypeRequired()) {
      moldCtrl.setValidators([Validators.required, Validators.maxLength(30)]);
    } else {
      moldCtrl.setValidators([Validators.maxLength(30)]);
    }
    moldCtrl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Make tread selection optional when mold type is Custom ('M').
   * When moldType === 'M' we clear the required validator on treadId so the UI
   * treats tread design as optional. When moldType is not 'M' and the tread control
   * is enabled, restore the required validator.
   */
  /**
   * Mirrors legacy getTreadDesignFlag() from WorkOrderDetailManagedBean for the creation modal scope.
   *
   * Legacy full signature:
   *   RETREAD_TREAD == 'REQUIRED'  → treadDescFlag === 'R'
   *   && processCode != AD
   *   && (processCode != RS || casingStatus == IN_STOCK)
   *
   * The RS + IN_STOCK branch is intentionally excluded here: IN_STOCK is only ever set on
   * already-persisted lines that have physically returned to stock. The backend never assigns
   * IN_STOCK for new lines (RS → RELEASED). In edit mode the RS special-code path disables the
   * tread control regardless, so the IN_STOCK condition would never reach the required-validator
   * logic. RS + IN_STOCK tread editing belongs to the full WOL detail view, not this modal.
   */
  private computeTreadDesignFlag(): boolean {
    const treadDescFlag = this.headerSettings?.treadDescFlag || '';
    const pcVal = this.lineWOForm.get('processCodeId')?.value;
    const code = typeof pcVal === 'object' ? pcVal?.code || pcVal?.value || '' : pcVal || '';
    const upper = String(code).toUpperCase();
    return treadDescFlag === 'R' && upper !== 'AD' && upper !== 'RS';
  }

  private updateTreadRequiredBasedOnMold(): void {
    const treadCtrl = this.lineWOForm.get('treadId');
    if (!treadCtrl) return;
    const moldVal = this.lineWOForm.get('moldType')?.value;
    if (moldVal === 'M') {
      // Custom mold: tread design optional
      treadCtrl.clearValidators();
      // Clear any existing errors that might have been set previously (e.g. required)
      try {
        treadCtrl.setErrors(null);
      } catch {
        // ignore
      }
      this.treadRequired = false;
    } else {
      if (treadCtrl.enabled && this.computeTreadDesignFlag()) {
        // RETREAD_TREAD == 'REQUIRED' && processCode is neither AD nor RS (unless RS+IN_STOCK)
        treadCtrl.setValidators([Validators.required]);
        this.treadRequired = true;
      } else {
        treadCtrl.clearValidators();
        this.treadRequired = false;
      }
    }
    treadCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private updateMfgDotControl(): void {
    const mfgDotCtrl = this.lineWOForm.get('mfgDot');
    if (!mfgDotCtrl) return;
    const base = [Validators.maxLength(4), optionalPattern(/^[A-Za-z0-9]*$/, 'alnum')];
    if (this.headerSettings?.isMfgRequiredOnCreate) {
      mfgDotCtrl.setValidators([Validators.required, ...base]);
    } else {
      mfgDotCtrl.setValidators(base);
    }
    mfgDotCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private updateRtDotValidators(): void {
    const rtCtrl = this.lineWOForm.get('rtDot');
    if (!rtCtrl) return;
    if (this.customerRtDot === 'N') {
      // Hidden in legacy: rendered="#{!workOrderDetail.rtdotHidden}" where rtdotHidden = (RT_DOT == 'N')
      rtCtrl.clearValidators();
      rtCtrl.setValue('', { emitEvent: false });
      rtCtrl.disable({ emitEvent: false });
    } else {
      // 'R', 'Y' or other: field is shown but never required at WO creation (no commonsValidator in legacy JSP).
      // RT_DOT='R' required check only occurs in LimitFailureManagedBean.reject() (CAM post-process, ntr>0).
      if (rtCtrl.disabled) rtCtrl.enable({ emitEvent: false });
      rtCtrl.setValidators([Validators.maxLength(7), optionalPattern(/^[A-Za-z0-9]*$/, 'alnum')]);
    }
    rtCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private updateLengthValidators(): void {
    const lengthCtrl = this.lineWOForm.get('lengthId');
    if (!lengthCtrl) return;
    if (this.headerSettings?.lengthFlag === 'R') {
      lengthCtrl.setValidators([Validators.required]);
    } else {
      // 'Y' = visible but optional, 'N' = hidden — no validator needed in either case
      lengthCtrl.clearValidators();
    }
    lengthCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private updateWOLNTR(): void {
    if (this.headerSettings?.ntrFlag === 'N') return;
    const ntrCtrl = this.lineWOForm.get('ntrId');
    const pcVal = this.lineWOForm.get('processCodeId')?.value;
    const code = typeof pcVal === 'object' ? pcVal?.code || '' : pcVal || '';
    if (ntrCtrl) {
      if (this.headerSettings?.ntrFlag === 'R' && code !== 'RO') {
        ntrCtrl.setValidators([Validators.required, Validators.maxLength(1), optionalPattern(/^[0-9]{1}$/, 'ntrDigit')]);
      } else {
        ntrCtrl.setValidators([Validators.maxLength(1), optionalPattern(/^[0-9]{1}$/, 'ntrDigit')]);
      }
      ntrCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  private loadStaticDropdowns(): void {
    if (!this.dealer?.id) return;
    this.makesLoading = true;
    this.createWorkOrderService.getMakes(this.dealer.id).subscribe({
      next: (d) => {
        this.makes = d;
        this.makesLoading = false;
      },
      error: () => {
        this.makesLoading = false;
      }
    });
    this.createWorkOrderService
      .getApplicationCodes(this.dealer.id)
      .subscribe({ next: (d) => (this.applicationOptions = d), error: () => {} });
    this.createWorkOrderService.getAxleCodes(this.dealer.id).subscribe({ next: (d) => (this.axleOptions = d), error: () => {} });
    this.createWorkOrderService.getWheelCodes(this.dealer.id).subscribe({ next: (d) => (this.wheelOptions = d), error: () => {} });
    this.createWorkOrderService.getWheelColors(this.dealer.id).subscribe({ next: (d) => (this.wheelColorOptions = d), error: () => {} });
    this.createWorkOrderService.getCasingGrades(this.dealer.id).subscribe({ next: (d) => (this.casingGradeOptions = d), error: () => {} });
    this.createWorkOrderService
      .getRejectReasons(this.dealer.id)
      .subscribe({ next: (d) => (this.rejectReasonOptions = d), error: () => {} });
    this.createWorkOrderService.getTreadLengths(this.dealer.id).subscribe({ next: (d) => (this.lengthOptions = d), error: () => {} });
  }

  private loadLineProcessCodes(customerId?: string): Observable<ProcessCode[]> {
    if (!this.dealer?.id) return of([]);
    const plantId = this.plant?.id;
    this.lineProcessCodesLoading = true;
    return this.createWorkOrderService.getLineProcessCodes(this.dealer.id, plantId, customerId).pipe(
      tap((data) => {
        this.lineProcessCodes = data;
        this.lineProcessCodesLoading = false;
      }),
      catchError(() => {
        this.lineProcessCodesLoading = false;
        return of([]);
      })
    );
  }

  private autoSelectMatchingProductionCustomer(): void {
    if (!this.selectedCustomerUuid || this.productionCustomers.length === 0) return;
    const match = this.productionCustomers.find((c) => c.id === this.selectedCustomerUuid);
    if (match) {
      this.lineWOForm.patchValue({ productionCustomerId: match }, { emitEvent: false });
    }
  }

  resetLineForm(): void {
    this.lineWOForm.reset({}, { emitEvent: false });
    this.series = [];
    this.sizes = [];
    this.treads = [];
    this.lineScProcessCode = false;
    this.lineNpProcessCode = false;
    const currentPc = this.lineWOForm.get('processCodeId')?.value;
    const pcCode = typeof currentPc === 'object' ? currentPc?.code || '' : currentPc || '';
    this.updateCasingValueVisibility(String(pcCode).toUpperCase());
    this.pendingCamViolation = null;
    this.lineCamRejected = false;
    this.lineCamViolationField = null;
    this.lineCamViolationDisposition = null;
    this.camDowngradeTreads = [];
    this.selectedDowngradeTread = null;
    this.camFixedViolationTypes.clear();
    this.camRecheckPendingAge = false;
    this.pendingSaveAction = null;
    ['seriesId', 'sizeId', 'treadId', 'application', 'axle', 'rtDot'].forEach((c) => {
      const ctrl = this.lineWOForm.get(c);
      if (ctrl?.disabled) ctrl.enable({ emitEvent: false });
    });
    this.updateMoldRadioControl();
  }

  /**
   * Selective field reset for "Save and Add Line" flow — retains certain fields based on settings.
   * MIGRATED FROM: WorkOrderDetailManagedBean.eraseAllMainFields() + setBarCodeAfterAddline()
   *
   * Field retention rules:
   * - ALWAYS cleared: barcode, mfgDot, rtDot, ntrId, systemGrade, overrideGrade, scrapCode, npCode,
   *   disposalCharge, casingStatus, treadDepthMin, treadDepthMax, repair, casingValue, CAM state
   * - ALWAYS kept: lengthId (user preference, no product dependency)
   * - CONDITIONALLY kept: camSpec (only when rememberFieldsOnAddLine — camSpec is computed from
   *   make/series/size/tread/workType, so clearing those makes it stale)
   * - CONDITIONALLY kept: brandNumber (based on rememberBrand: 'A'=always, 'S'=stock codes BS/DS, 'C'=customer/non-stock)
   * - CONDITIONALLY kept: casingNotes (based on rememberCasingNote: 'A'=always, 'S'=stock codes BS/DS, 'C'=customer/non-stock)
   * - CONDITIONALLY kept: processCodeId, workType, makeId, seriesId, sizeId, treadId, moldType,
   *   application, axle, wheelType, wheelColor (based on plant.rememberFieldsOnAddLine === 'Y')
   */
  private resetForNextLine(): void {
    const currentBarcode = this.lineWOForm.get('barcode')?.value || '';
    const currentProcessCode = this.lineWOForm.get('processCodeId')?.value;
    const pcCode =
      typeof currentProcessCode === 'object' ? currentProcessCode?.code || currentProcessCode?.value || '' : currentProcessCode || '';
    const pcUpper = String(pcCode).toUpperCase();
    const isStockProcessCode = pcUpper === 'BS' || pcUpper === 'DS';

    // Determine which fields to retain
    const rememberFields = this.plant?.rememberFieldsOnAddLine === 'Y';

    const shouldKeepBrand =
      this.rememberBrand === 'A' ||
      (this.rememberBrand === 'S' && isStockProcessCode) ||
      (this.rememberBrand === 'C' && !isStockProcessCode);
    const shouldKeepCasingNote =
      this.rememberCasingNote === 'A' ||
      (this.rememberCasingNote === 'S' && isStockProcessCode) ||
      (this.rememberCasingNote === 'C' && !isStockProcessCode);

    // Save values to retain before clearing
    // lengthId: always retained — user preference with no product dependency
    // camSpec: retained only when product fields are kept (rememberFields), since camSpec is
    //   computed from make/series/size/tread/workType/application/axle — clearing those inputs
    //   makes the cached camSpec stale and misleading
    const retainedValues: Record<string, any> = {};
    retainedValues['lengthId'] = this.lineWOForm.get('lengthId')?.value;
    if (rememberFields) {
      retainedValues['camSpec'] = this.lineWOForm.get('camSpec')?.value;
    }
    if (shouldKeepBrand) {
      retainedValues['brandNumber'] = this.lineWOForm.get('brandNumber')?.value || '';
    }
    if (shouldKeepCasingNote) {
      retainedValues['casingNotes'] = this.lineWOForm.get('casingNotes')?.value || '';
    }
    if (rememberFields) {
      retainedValues['processCodeId'] = this.lineWOForm.get('processCodeId')?.value;
      retainedValues['workType'] = this.lineWOForm.get('workType')?.value;
      retainedValues['makeId'] = this.lineWOForm.get('makeId')?.value;
      retainedValues['seriesId'] = this.lineWOForm.get('seriesId')?.value;
      retainedValues['sizeId'] = this.lineWOForm.get('sizeId')?.value;
      retainedValues['treadId'] = this.lineWOForm.get('treadId')?.value;
      retainedValues['moldType'] = this.lineWOForm.get('moldType')?.value;
      retainedValues['application'] = this.lineWOForm.get('application')?.value;
      retainedValues['axle'] = this.lineWOForm.get('axle')?.value;
      retainedValues['wheelType'] = this.lineWOForm.get('wheelType')?.value;
      retainedValues['wheelColor'] = this.lineWOForm.get('wheelColor')?.value;
      retainedValues['productionCustomerId'] = this.lineWOForm.get('productionCustomerId')?.value;
    }

    // Reset entire form
    this.lineWOForm.reset({}, { emitEvent: false });

    // Clear CAM state
    this.pendingCamViolation = null;
    this.lineCamRejected = false;
    this.lineCamViolationField = null;
    this.lineCamViolationDisposition = null;
    this.camDowngradeTreads = [];
    this.selectedDowngradeTread = null;
    this.camFixedViolationTypes.clear();
    this.camRecheckPendingAge = false;
    this.pendingSaveAction = null;

    // Re-enable potentially disabled controls
    ['seriesId', 'sizeId', 'treadId', 'application', 'axle', 'rtDot'].forEach((c) => {
      const ctrl = this.lineWOForm.get(c);
      if (ctrl?.disabled) ctrl.enable({ emitEvent: false });
    });

    // Restore retained values
    if (!rememberFields) {
      // When fields are NOT remembered, clear dropdown arrays
      this.series = [];
      this.sizes = [];
      this.treads = [];
      this.lineScProcessCode = false;
      this.lineNpProcessCode = false;
    }

    // Patch retained values back
    if (Object.keys(retainedValues).length > 0) {
      this.lineWOForm.patchValue(retainedValues, { emitEvent: false });
    }

    // Update derived UI state based on restored process code
    if (rememberFields && retainedValues['processCodeId']) {
      this.updateCasingValueVisibility(pcUpper);
    } else {
      this.updateCasingValueVisibility('');
    }

    this.updateMoldRadioControl();

    // Auto-increment barcode if plant setting is enabled
    // MIGRATED FROM: WorkOrderDetailManagedBean.setBarCodeAfterAddline()
    if (this.plant?.autoIncBarcode === 'Y' && currentBarcode) {
      this.barcodeValidationLoading = true;
      this.createWorkOrderService.getNextBarcode(this.dealer.id, currentBarcode).subscribe({
        next: (response) => {
          this.barcodeValidationLoading = false;
          const barcodeCtrl = this.lineWOForm.get('barcode');
          const nextBarcode = response.nextBarcode;
          if (nextBarcode && !response.errorMessage) {
            // Backend guarantees nextBarcode is <= 10 chars (varchar(10) enforced server-side).
            // Only check in-memory duplicates (sessionLines/existingLines not yet persisted).
            const normalizedVal = nextBarcode.toUpperCase();
            const existingBarcodeSet = new Set([
              ...this.existingLines.map((l) => (l.barcode || '').toUpperCase()),
              ...this.sessionLines.map((l) => (l.barcode || '').toUpperCase())
            ]);
            this.lineWOForm.patchValue({ barcode: nextBarcode }, { emitEvent: false });
            if (existingBarcodeSet.has(normalizedVal)) {
              barcodeCtrl?.setErrors({ barcodeExistsLocal: true });
              barcodeCtrl?.markAsTouched();
            }
          } else {
            // Could not compute or exceeds max length — keep current barcode for manual edit
            this.lineWOForm.patchValue({ barcode: currentBarcode }, { emitEvent: false });
            barcodeCtrl?.setErrors({ barcodeNotIncrementable: true });
            barcodeCtrl?.markAsTouched();
          }
        },
        error: (err) => {
          this.barcodeValidationLoading = false;
          console.error('Failed to compute next barcode:', err);
          this.lineWOForm.patchValue({ barcode: currentBarcode }, { emitEvent: false });
          const barcodeCtrl = this.lineWOForm.get('barcode');
          barcodeCtrl?.setErrors({ barcodeNotIncrementable: true });
          barcodeCtrl?.markAsTouched();
        }
      });
    }
  }

  // -------- Form value changes --------

  private setupFormValueChanges(): void {
    this.lineWOForm.get('makeId')?.valueChanges.subscribe((val) => {
      if (val) {
        this.onMakeChange(val);
        this.updateLineControlsForMake(val);
      } else {
        this.lineWOForm.patchValue({ seriesId: '', sizeId: '', treadId: '' }, { emitEvent: false });
        this.updateLineControlsForMake(null);
      }
    });

    this.lineWOForm.get('seriesId')?.valueChanges.subscribe((val) => {
      if (val) this.onSeriesChange(val);
      else this.lineWOForm.patchValue({ sizeId: '', treadId: '' }, { emitEvent: false });
    });

    this.lineWOForm.get('sizeId')?.valueChanges.subscribe((val) => {
      if (val) {
        this.onSizeChange(val);
        // Size change invalidates any tread-derived application/axle selection from the prior size.
        this.lineWOForm.patchValue({ application: '', axle: '' }, { emitEvent: false });
        // Keep CAM/spec and derived values in sync as soon as size is selected.
        this.triggerLineCamSpecLookup();
        this.gradeAndWorktypeUpdate();
      } else {
        this.lineWOForm.patchValue({ treadId: '', application: '', axle: '' }, { emitEvent: false });
        this.lineWOForm.patchValue({ camSpec: '', systemGrade: '', casingValue: '' }, { emitEvent: false });
      }
    });

    this.lineWOForm.get('moldType')?.valueChanges.subscribe(() => {
      // Legacy: idPreMoldCustomMold onchange clears treadDesc, application AND axle
      // oncomplete="emptyFieldWithoutFireOnChange($('xnetForm:idTreadDesc'));
      //             emptyFieldWithoutFireOnChange($('xnetForm:idApplication'));
      //             emptyFieldWithoutFireOnChange($('xnetForm:idAxle'));"
      this.lineWOForm.patchValue({ treadId: '', application: '', axle: '' }, { emitEvent: false });
      const sizeVal = this.lineWOForm.get('sizeId')?.value;
      if (sizeVal) this.onSizeChange(sizeVal);
      // Adjust tread requirement when mold type changes (custom mold 'M' makes tread optional)
      this.updateTreadRequiredBasedOnMold();
    });

    this.lineWOForm.get('processCodeId')?.valueChanges.subscribe((pcVal) => {
      const code = typeof pcVal === 'object' ? pcVal?.code || pcVal?.value || '' : pcVal || '';
      const upper = String(code).toUpperCase();
      const workType = this.getWorkTypeForProcessCode(upper);
      this.lineWOForm.patchValue({ workType }, { emitEvent: false });

      const specialCodes = ['IO', 'SC', 'NP', 'RO', 'SO', 'RS', 'DT'];
      const isSpecial = specialCodes.includes(upper);

      this.lineScProcessCode = upper === 'SC';
      this.lineNpProcessCode = upper === 'NP';

      const scrapCtrl = this.lineWOForm.get('scrapCode');
      const npCtrl = this.lineWOForm.get('npCode');
      if (this.lineScProcessCode) {
        scrapCtrl?.setValidators([Validators.required]);
      } else {
        scrapCtrl?.clearValidators();
        scrapCtrl?.setValue('', { emitEvent: false });
      }
      scrapCtrl?.updateValueAndValidity({ emitEvent: false });

      if (this.lineNpProcessCode) {
        npCtrl?.setValidators([Validators.required]);
      } else {
        npCtrl?.clearValidators();
        npCtrl?.setValue('', { emitEvent: false });
      }
      npCtrl?.updateValueAndValidity({ emitEvent: false });

      if (this.lineScProcessCode && this.customerDisposalChargesFk) {
        this.createWorkOrderService.getDisposalChargeById(this.dealer.id, this.customerDisposalChargesFk).subscribe({
          next: (dc) => this.lineWOForm.patchValue({ disposalCharge: dc ?? null }, { emitEvent: false }),
          error: () => this.lineWOForm.patchValue({ disposalCharge: null }, { emitEvent: false })
        });
      } else {
        this.lineWOForm.patchValue({ disposalCharge: null }, { emitEvent: false });
      }

      const treadCtrl = this.lineWOForm.get('treadId');
      const appCtrl = this.lineWOForm.get('application');
      const axleCtrl = this.lineWOForm.get('axle');

      if (isSpecial) {
        treadCtrl?.setValue('', { emitEvent: false });
        treadCtrl?.disable({ emitEvent: false });
        appCtrl?.setValue('', { emitEvent: false });
        appCtrl?.disable({ emitEvent: false });
        axleCtrl?.setValue('', { emitEvent: false });
        axleCtrl?.disable({ emitEvent: false });
        // Legacy: moldType readonly when specialProcessCode — mirrors JSP
        // readonly="#{!workOrderDetail.editable || workOrderDetail.specialProcessCode}"
        if (this.showMoldRadio) {
          this.lineWOForm.get('moldType')?.disable({ emitEvent: false });
        }
      } else {
        treadCtrl?.enable({ emitEvent: false });
        appCtrl?.enable({ emitEvent: false });
        axleCtrl?.enable({ emitEvent: false });
        if (this.showMoldRadio) {
          this.lineWOForm.get('moldType')?.enable({ emitEvent: false });
        }
        const sizeVal = this.lineWOForm.get('sizeId')?.value;
        if (sizeVal) this.onSizeChange(sizeVal);
        else this.lineWOForm.patchValue({ treadId: '' }, { emitEvent: false });
      }

      this.updateSendToServicePostOnWheelSelected();
      this.triggerLineCamSpecLookup();
      this.updateWOLNTR();
      this.updateCasingValueVisibility(upper);
      this.gradeAndWorktypeUpdate();
      this.updateMoldTypeRequired();
      this.updateTreadRequiredBasedOnMold();
    });

    this.lineWOForm.get('productionCustomerId')?.valueChanges.subscribe((custVal) => {
      const customerId = typeof custVal === 'object' && custVal !== null ? custVal?.id || custVal?.customerPk || '' : custVal || '';
      if (!this.dealer?.id) return;
      const plantId = this.plant?.id;
      this.lineProcessCodesLoading = true;
      this.createWorkOrderService.getLineProcessCodes(this.dealer.id, plantId, customerId || undefined).subscribe({
        next: (data) => {
          this.lineProcessCodes = data;
          this.lineProcessCodesLoading = false;
          // If currently selected process code is no longer valid (e.g. CC/CP removed), clear it
          const currentProcessCode = this.lineWOForm.get('processCodeId')?.value;
          if (currentProcessCode) {
            const codeStr =
              typeof currentProcessCode === 'object' ? currentProcessCode?.code || currentProcessCode?.label || '' : currentProcessCode;
            const stillValid = this.lineProcessCodes.some((pc) => pc.code === codeStr || pc.id === currentProcessCode);
            if (!stillValid) {
              this.lineWOForm.patchValue({ processCodeId: '' }, { emitEvent: false });
            }
          }
        },
        error: () => {
          this.lineProcessCodesLoading = false;
        }
      });
      // customerId is one of the cam spec inputs, so re-fetch when customer changes
      this.triggerLineCamSpecLookup();
      // Re-validate NTR / MFG DOT CAM specs when production customer changes
      const ntrValue = this.lineWOForm.get('ntrId')?.value;
      const mfgDotValue = (this.lineWOForm.get('mfgDot')?.value || '').trim();
      const pcVal = this.lineWOForm.get('processCodeId')?.value;
      const pcCode = typeof pcVal === 'object' ? pcVal?.code || pcVal?.value || '' : pcVal || '';
      if (ntrValue && pcCode) {
        this.lineWOForm.get('ntrId')?.setErrors(null);
        this.checkNtrCamViolation(String(ntrValue));
      }
      if (mfgDotValue && pcCode) {
        this.lineWOForm.get('mfgDot')?.setErrors(null);
        this.checkMfgDotCamViolation(mfgDotValue);
      }
    });

    this.lineWOForm.get('treadId')?.valueChanges.subscribe(() => {
      const treadValue = this.lineWOForm.get('treadId')?.value;
      const matchedAxle = this.axleOptions.find((option) => option.id === treadValue?.axleId) ?? null;
      const matchedApp = this.applicationOptions.find((option) => option.id === treadValue?.applicationId) ?? null;
      // Use emitEvent: false so axle/application valueChanges do not cascade into gradeAndWorktypeUpdate
      // and triggerLineCamSpecLookup prematurely — both are called explicitly below.
      // Using null (not undefined) when no match is found ensures the controls are cleared deterministically.
      this.lineWOForm.patchValue({ axle: matchedAxle, application: matchedApp }, { emitEvent: false });
      this.triggerLineCamSpecLookup();
      this.gradeAndWorktypeUpdate();
      // AD worktype: moldType required only when tread is filled — re-evaluate on every tread change
      this.updateMoldTypeRequired();
    });

    this.lineWOForm.get('application')?.valueChanges.subscribe(() => {
      this.triggerLineCamSpecLookup();
      this.gradeAndWorktypeUpdate();
    });
    this.lineWOForm.get('axle')?.valueChanges.subscribe(() => {
      this.triggerLineCamSpecLookup();
      this.gradeAndWorktypeUpdate();
    });
    this.lineWOForm.get('wheelType')?.valueChanges.subscribe(() => this.updateSendToServicePostOnWheelSelected());
    this.lineWOForm.get('wheelColor')?.valueChanges.subscribe(() => this.updateSendToServicePostOnWheelSelected());
    this.lineWOForm.get('ntrId')?.valueChanges.subscribe(() => this.checkRtDotRequiredForReject());
    this.lineWOForm.get('rtDot')?.valueChanges.subscribe(() => this.checkRtDotRequiredForReject());
  }

  private setupDebouncedValidators(): void {
    this.lineWOForm
      .get('ntrId')
      ?.valueChanges.pipe(debounceTime(1500))
      .subscribe((val) => {
        this.ntrChangeSubject.next(val || '');
      });
    this.ntrChangeSubject.subscribe((val) => this.checkNtrCamViolation(val));

    this.lineWOForm
      .get('mfgDot')
      ?.valueChanges.pipe(debounceTime(1500))
      .subscribe((val) => {
        this.mfgDotChangeSubject.next(val || '');
        const rtDot = (this.lineWOForm.get('rtDot')?.value || '') as string;
        if (rtDot) {
          this.checkRtDotValidation(rtDot);
        }
      });
    this.mfgDotChangeSubject.subscribe((val) => this.checkMfgDotCamViolation(val));

    this.barcodeChangeSubject.subscribe((val) => {
      if (!val || !this.dealer?.id) return;
      val = val.toUpperCase();
      if (this.headerSettings?.barcodeLeadingZero) {
        val = this.createWorkOrderService.applyLeadingZeroPadding(val, true);
      }
      this.lineWOForm.patchValue({ barcode: val }, { emitEvent: false });
      // Check for duplicates against already-added lines in the current work order (in-memory)
      const editLineId = this.isEditMode && this.editingLine?.id ? this.editingLine.id : null;
      const normalizedVal = val.toUpperCase();
      const existingBarcodeSet = new Set([
        ...this.existingLines.filter((l) => !editLineId || l.id !== editLineId).map((l) => (l.barcode || '').toUpperCase()),
        ...this.sessionLines.map((l) => (l.barcode || '').toUpperCase())
      ]);
      const barcodeCtrl = this.lineWOForm.get('barcode');
      if (existingBarcodeSet.has(normalizedVal)) {
        const existingErrors = barcodeCtrl?.errors ? { ...barcodeCtrl.errors } : {};
        barcodeCtrl?.setErrors({ ...existingErrors, barcodeExistsLocal: true });
        return;
      }
      // Clear any stale in-memory duplicate error before server validation
      if (barcodeCtrl?.hasError('barcodeExistsLocal')) {
        const errors = { ...barcodeCtrl.errors };
        delete errors['barcodeExistsLocal'];
        barcodeCtrl.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
      this.barcodeValidationLoading = true;
      this.createWorkOrderService
        .validateLineBarcode(this.dealer.id, val)
        .pipe(finalize(() => (this.barcodeValidationLoading = false)))
        .subscribe({
          next: (res) => {
            const ctrl = this.lineWOForm.get('barcode');
            if (!res.valid) {
              const existing = ctrl?.errors ? { ...ctrl.errors } : {};
              ctrl?.setErrors({ ...existing, barcodeExists: true });
            } else {
              if (ctrl?.hasError('barcodeExists')) {
                const errors = { ...ctrl.errors };
                delete errors['barcodeExists'];
                ctrl.setErrors(Object.keys(errors).length > 0 ? errors : null);
              }
            }
          },
          error: () => {}
        });
    });

    this.lineWOForm
      .get('rtDot')
      ?.valueChanges.pipe(debounceTime(1500))
      .subscribe((val) => {
        this.rtDotChangeSubject.next(val || '');
      });
    this.rtDotChangeSubject.subscribe((val) => this.checkRtDotValidation(val));
  }

  // -------- Cascade dropdown handlers --------

  private onMakeChange(makeVal: any): void {
    const makeId = this.extractId(makeVal);
    if (!makeId || !this.dealer?.id) return;
    this.lineWOForm.patchValue({ seriesId: '', sizeId: '', treadId: '' }, { emitEvent: false });
    this.seriesLoading = true;
    this.createWorkOrderService.getSeries(this.dealer.id, makeId).subscribe({
      next: (d) => {
        this.series = d;
        this.seriesLoading = false;
        this.sizes = [];
        this.treads = [];
      },
      error: () => {
        this.seriesLoading = false;
      }
    });
    this.sizes = [];
    this.treads = [];
  }

  private onSeriesChange(seriesVal: any): void {
    const seriesId = this.extractId(seriesVal);
    const makeId = this.extractId(this.lineWOForm.get('makeId')?.value);
    if (!seriesId || !makeId || !this.dealer?.id) return;
    this.lineWOForm.patchValue({ sizeId: '', treadId: '' }, { emitEvent: false });
    const plantId = this.plant?.id;
    this.sizesLoading = true;
    this.createWorkOrderService.getSizes(this.dealer.id, makeId, seriesId, plantId).subscribe({
      next: (d) => {
        this.sizes = d;
        this.sizesLoading = false;
        this.treads = [];
      },
      error: () => {
        this.sizesLoading = false;
      }
    });
    this.treads = [];
  }

  private onSizeChange(sizeVal: any): void {
    const sizeId = this.extractId(sizeVal);
    const plantId = this.plant?.id;
    if (!sizeId || !plantId || !this.dealer?.id) return;
    this.lineWOForm.patchValue({ treadId: '' }, { emitEvent: false });
    if (this.showMoldRadio) {
      const moldCtrl = this.lineWOForm.get('moldType');
      if (!moldCtrl?.enabled || !moldCtrl?.value) {
        this.treads = [];
        return;
      }
    }
    // If the tread control has been disabled (e.g. by a special process code),
    // avoid calling the backend and clear the treads list.
    const treadCtrl = this.lineWOForm.get('treadId');
    if (treadCtrl && !treadCtrl.enabled) {
      this.treads = [];
      this.treadsLoading = false;
      return;
    }
    const makeId = this.extractId(this.lineWOForm.get('makeId')?.value) || undefined;
    const seriesId = this.extractId(this.lineWOForm.get('seriesId')?.value) || undefined;
    const moldType = this.lineWOForm.get('moldType')?.value || undefined;
    const pcVal = this.lineWOForm.get('processCodeId')?.value;
    const pcCode = typeof pcVal === 'object' ? pcVal?.code || pcVal?.value || '' : pcVal || '';
    const isSpecialPc = ['IO', 'SC', 'NP', 'RO', 'SO', 'RS', 'DT'].includes(String(pcCode).toUpperCase());
    if (!isSpecialPc) {
      this.treadsLoading = true;
      this.createWorkOrderService
        .getTreads(this.dealer.id, sizeId, plantId, this.preferredTreadSpecPk ?? undefined, makeId, seriesId, moldType)
        .subscribe({
          next: (d) => {
            this.treads = d;
            this.treadsLoading = false;
          },
          error: () => {
            this.treadsLoading = false;
          }
        });
    }
  }

  private updateLineControlsForMake(makeVal: any): void {
    const makeId = this.extractId(makeVal);
    let selectedMake: any = null;
    if (makeId) selectedMake = this.makes.find((m) => m.id === makeId) || null;
    else if (makeVal && typeof makeVal === 'object') selectedMake = makeVal;

    const isInvalid = selectedMake ? selectedMake.isValid === 'N' || selectedMake.isValid === 'n' : false;
    if (isInvalid) {
      this.lineWOForm.patchValue({ seriesId: '', sizeId: '', treadId: '' }, { emitEvent: false });
      ['seriesId', 'sizeId', 'treadId'].forEach((f) => this.lineWOForm.get(f)?.disable({ emitEvent: false }));
      if (this.showMoldRadio) {
        const m = this.lineWOForm.get('moldType');
        m?.setValue('', { emitEvent: false });
        m?.disable({ emitEvent: false });
      }
      // Recompute tread requirement when controls are disabled due to invalid make
      this.updateTreadRequiredBasedOnMold();
    } else {
      ['seriesId', 'sizeId'].forEach((f) => this.lineWOForm.get(f)?.enable({ emitEvent: false }));
      // Only re-enable treadId and moldType when process code has not locked them
      // (special codes disable tread/app/axle and make moldType readonly when shown).
      const pcVal = this.lineWOForm.get('processCodeId')?.value;
      const pcCode = typeof pcVal === 'object' ? pcVal?.code || pcVal?.value || '' : pcVal || '';
      const isSpecialPc = ['IO', 'SC', 'NP', 'RO', 'SO', 'RS', 'DT'].includes(String(pcCode).toUpperCase());
      if (!isSpecialPc) {
        this.lineWOForm.get('treadId')?.enable({ emitEvent: false });
      }
      if (this.showMoldRadio) {
        if (!isSpecialPc) {
          this.lineWOForm.get('moldType')?.enable({ emitEvent: false });
        } else {
          // Ensure moldType stays disabled when a special process code (e.g. RO) is selected
          this.lineWOForm.get('moldType')?.disable({ emitEvent: false });
        }
      }
      // Recompute tread requirement when controls are re-enabled for a valid make
      this.updateTreadRequiredBasedOnMold();
    }
  }

  private updateSendToServicePostOnWheelSelected(): void {
    const pcVal = this.lineWOForm.get('processCodeId')?.value;
    const code = typeof pcVal === 'object' ? pcVal?.code || '' : pcVal || '';
    const wheelType = this.lineWOForm.get('wheelType')?.value;
    const wheelColor = this.lineWOForm.get('wheelColor')?.value;
    const hasWheel = (wheelType && wheelType !== '') || (wheelColor && wheelColor !== '');
    const flag = code.toUpperCase() === 'SO' && hasWheel ? 'Y' : 'N';
    this.lineWOForm.patchValue({ sendToServicePostFlag: flag }, { emitEvent: false });
  }

  private updateCasingValueVisibility(upperCode: string): void {
    const show = this.isSAZone || ['CP', 'CC', 'RS'].includes(upperCode);
    this.showCasingValueField = show;
    const ctrl = this.lineWOForm.get('casingValue');
    if (!ctrl) return;
    if (show) {
      ctrl.setValidators([Validators.required, Validators.maxLength(9), optionalNumber(0)]);
      if (ctrl.disabled) ctrl.enable({ emitEvent: false });
    } else {
      ctrl.clearValidators();
      if (!ctrl.disabled) ctrl.disable({ emitEvent: false });
      ctrl.patchValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private applyFormatNumber(nStr: string): string {
    if (nStr === '') return '0.00';
    const x = nStr.split('.');
    if (x.length > 1) {
      const x2 = '.' + x[1].substr(0, 2).padEnd(2, '0');
      return x[0] + x2;
    }
    if (x[0].length > 6) {
      return this.applyFormatNumber(x[0].substr(0, 6) + '.' + x[0].substr(3, x[0].length - 6));
    }
    return x[0] + '.00';
  }

  // -------- Business logic --------

  private getWorkTypeForProcessCode(upper: string): string {
    const map: Record<string, string> = {
      BS: 'RR',
      DS: 'RR',
      CP: 'RR',
      CC: 'RR',
      RR: 'RR',
      RO: 'RO',
      SO: 'SO',
      IO: 'IO',
      AD: 'AD',
      SC: 'SC',
      NP: 'NP',
      DT: 'DT',
      RS: 'RS'
    };
    return map[upper] || '';
  }

  private gradeAndWorktypeUpdate(): void {
    const pcVal = this.lineWOForm.get('processCodeId')?.value;
    const code = typeof pcVal === 'object' ? pcVal?.code || pcVal?.value || '' : pcVal || '';
    const upper = String(code).toUpperCase();
    const workType = this.getWorkTypeForProcessCode(upper);
    this.lineWOForm.patchValue({ workType }, { emitEvent: false });
    if (!this.dealer?.id || !upper) return;

    const prodCustVal = this.lineWOForm.get('productionCustomerId')?.value;
    const sizeVal = this.lineWOForm.get('sizeId')?.value;
    const customerUuid = this.extractId(prodCustVal);
    const casingSizeUuid = this.extractId(sizeVal);
    const casingMakeFk = this.extractId(this.lineWOForm.get('makeId')?.value) || undefined;
    const mfgDot = this.lineWOForm.get('mfgDot')?.value || undefined;
    const ntrRaw = this.lineWOForm.get('ntrId')?.value;
    const ntr = ntrRaw ? String(ntrRaw) || undefined : undefined;
    const tireAxle = this.lineWOForm.get('axle')?.value || undefined;

    this.createWorkOrderService
      .getSystemGrade(this.dealer.id, upper, customerUuid, casingSizeUuid, casingMakeFk, mfgDot, ntr, tireAxle)
      .subscribe({
        next: (res) => this.lineWOForm.patchValue({ systemGrade: res?.grade ?? '' }, { emitEvent: false }),
        error: () => this.lineWOForm.patchValue({ systemGrade: '' }, { emitEvent: false })
      });

    if (customerUuid && casingSizeUuid) {
      this.createWorkOrderService.getCasingValue(this.dealer.id, upper, customerUuid, casingSizeUuid).subscribe({
        next: (res) => {
          const raw = res?.casingValue ?? '';
          const formatted = this.applyFormatNumber(String(raw));
          this.lineWOForm.patchValue({ casingValue: formatted }, { emitEvent: false });
        },
        error: () => this.lineWOForm.patchValue({ casingValue: '' }, { emitEvent: false })
      });
    }
  }

  private triggerLineCamSpecLookup(): void {
    const workType = this.lineWOForm.get('workType')?.value;
    if (!workType || !this.dealer?.id) return;
    const makeVal = this.lineWOForm.get('makeId')?.value;
    const seriesVal = this.lineWOForm.get('seriesId')?.value;
    const sizeVal = this.lineWOForm.get('sizeId')?.value;
    const treadVal = this.lineWOForm.get('treadId')?.value;
    const prodCustVal = this.lineWOForm.get('productionCustomerId')?.value;
    const treadId = typeof treadVal === 'object' ? treadVal?.id : treadVal;
    const selectedTread = treadId ? this.treads.find((t) => t.id === treadId) : null;
    const m3Flag = selectedTread?.m3Flag === 'Y';
    const camRequest: CamRequest = {
      make: (typeof makeVal === 'object' ? makeVal?.name : null) || null,
      series: (typeof seriesVal === 'object' ? seriesVal?.value : null) || null,
      size: (typeof sizeVal === 'object' ? sizeVal?.value : null) || null,
      tread: (typeof treadVal === 'object' ? treadVal?.value : null) || null,
      workType,
      application: this.lineWOForm.get('application')?.value?.value || null,
      axle: this.lineWOForm.get('axle')?.value?.value || null,
      customerId: this.extractId(prodCustVal) || '',
      customerCode: (typeof prodCustVal === 'object' ? prodCustVal?.code : null) || null,
      ntr: this.lineWOForm.get('ntrId')?.value || null,
      mdot: this.lineWOForm.get('mfgDot')?.value || null,
      m3Flag,
      ignoreCheckCamSpecNtr: false,
      ignoreCheckCamSpecAge: false
    };
    this.createWorkOrderService.getMatchingCamSpec(this.dealer.id, camRequest).subscribe({
      next: (res) => this.lineWOForm.patchValue({ camSpec: res?.name || '' }, { emitEvent: false }),
      error: () => this.lineWOForm.patchValue({ camSpec: '' }, { emitEvent: false })
    });
  }

  private checkNtrCamViolation(ntrValue: string): void {
    const ntrNum = ntrValue ? parseInt(ntrValue, 10) : 0;
    const checkCamWoEntry = this.plant?.ckCamSpecWo === 'Y';
    if (!checkCamWoEntry || !ntrValue || isNaN(ntrNum) || ntrNum === 0) {
      this.lineWOForm.patchValue({ rtDot: '' }, { emitEvent: false });
      this.pendingCamViolation = null;
      this.gradeAndWorktypeUpdate();
      return;
    }
    this.lineWOForm.patchValue({ ignoreCheckCamSpecNtr: 'N' }, { emitEvent: false });
    const camReq = this.buildCamRequest({ ntr: ntrNum, mdot: this.lineWOForm.get('mfgDot')?.value || null });
    console.log('Checking NTR CAM violation with request:', camReq);
    this.lineNtrCamValidating = true;
    this.createWorkOrderService.getMatchingCamSpec(this.dealer.id, camReq).subscribe({
      next: (response) => {
        this.lineNtrCamValidating = false;

        console.log('Received CAM spec response for NTR check:', response);
        const treadVal = this.lineWOForm.get('treadId')?.value;
        const treadId = typeof treadVal === 'object' ? treadVal?.id : treadVal;
        const selectedTread = treadId ? this.treads.find((t) => t.id === treadId) : null;
        const m3Flag = selectedTread?.m3Flag === 'Y';
        const violation = this.createWorkOrderService.checkv4NtrCamSpecs(
          {
            ntrId: this.lineWOForm.get('ntrId')?.value,
            workType: this.lineWOForm.get('workType')?.value || '',
            ignoreCheckCamSpecNtr: this.lineWOForm.get('ignoreCheckCamSpecNtr')?.value || '',
            application: this.lineWOForm.get('application')?.value || null
          },
          response,
          m3Flag
        );
        // const ntrCtrl = this.lineWOForm.get('ntrId');
        if (violation?.ntrInError) {
          // If an AGE violation was being shown but not yet resolved, preserve it so recheckCamFields() re-validates it on save.
          if (this.pendingCamViolation?.type === 'age') {
            this.camFixedViolationTypes.add('age');
          }
          this.pendingCamViolation = {
            type: 'ntr',
            value: String(violation.value ?? ''),
            limit: String(violation.limit ?? ''),
            action: violation.action ?? '',
            disposition: violation.disposition ?? '',
            treads: violation.treads ?? [],
            application: violation.application ?? null,
            axle: violation.axle ?? null
          };
          // Compute downgrade tread options: intersection of authorised treads (this.treads) and CAM spec tread list.
          // Mirrors LimitFailureManagedBean.getDownGradeTreadDisplay() NTR branch: finds treadDescBean by name
          // (case-insensitive) and checks existeInTheList(treadDescBean.getTreadDesc(), treadAuthorized).
          if (this.pendingCamViolation.action?.toUpperCase() === 'DOWNGRADE') {
            const specTreadsLower = (violation.treads ?? []).map((n) => n?.toLowerCase());
            this.camDowngradeTreads = this.treads.filter((t) => specTreadsLower.includes(t.value?.toLowerCase()));
          } else {
            this.camDowngradeTreads = [];
          }
          this.selectedDowngradeTread = this.camDowngradeTreads[0] ?? null;
          this.lineCamRejected = false;
          this.focusCamViolationBanner();
          // A new violation was found — cancel any deferred save; user must resolve it first.
          this.pendingSaveAction = null;
          this.camRecheckPendingAge = false;
          if (this.lineScProcessCode && violation.action?.toUpperCase() === 'REJECT' && violation.disposition?.toUpperCase() === 'SCRAP') {
            this.createWorkOrderService.getDisposalChargeByDescription(this.dealer.id, violation.disposition!).subscribe({
              next: (dc) => this.lineWOForm.patchValue({ disposalCharge: dc ?? null }, { emitEvent: false }),
              error: () => this.lineWOForm.patchValue({ disposalCharge: null }, { emitEvent: false })
            });
          } else {
            this.lineWOForm.patchValue({ disposalCharge: null }, { emitEvent: false });
          }
        } else {
          // If an AGE violation was being shown but not yet resolved, preserve it so recheckCamFields() re-validates it on save.
          if (this.pendingCamViolation?.type === 'age') {
            this.camFixedViolationTypes.add('age');
          }
          this.pendingCamViolation = null;
          this.camDowngradeTreads = [];
          this.selectedDowngradeTread = null;
          this.gradeAndWorktypeUpdate();
          // NTR passed — remove from pending rechecks, then chain to AGE or proceed to save.
          this.camFixedViolationTypes.delete('ntr');
          if (this.camRecheckPendingAge) {
            this.camRecheckPendingAge = false;
            const mfgDotValue = (this.lineWOForm.get('mfgDot')?.value || '').trim();
            this.checkMfgDotCamViolation(mfgDotValue);
          } else {
            this.executePendingSaveIfReady();
          }
        }
      },
      error: () => {
        this.lineNtrCamValidating = false;
        this.gradeAndWorktypeUpdate();
      }
    });
  }

  private checkMfgDotCamViolation(mfgDotValue: string): void {
    const mfgDot = (mfgDotValue || '').trim();
    const mfgDotCtrl = this.lineWOForm.get('mfgDot');
    // If the field was cleared and it's not required by header settings, clear any format errors and skip CAM checks.
    if (!mfgDot) {
      // headerSettings.isMfgRequiredOnCreate controls whether the field is required; if it's not required,
      // a cleared field should be considered valid and all errors removed.
      if (!this.headerSettings?.isMfgRequiredOnCreate) {
        if (mfgDotCtrl) {
          mfgDotCtrl.setErrors(null);
        }
        if (this.pendingCamViolation?.type === 'ntr') {
          this.camFixedViolationTypes.add('ntr');
        }
        this.pendingCamViolation = null;
        this.gradeAndWorktypeUpdate();
        return;
      }
    }
    if (mfgDot && this.headerSettings && this.createWorkOrderService.validateMfgDotOnly(mfgDot, this.headerSettings)) {
      if (mfgDotCtrl?.hasError('mfgDotFormat')) {
        const { ...rem } = mfgDotCtrl.errors as Record<string, any>;
        mfgDotCtrl.setErrors(Object.keys(rem).length ? rem : null);
      }
      const checkCamWoEntry = this.plant?.ckCamSpecWo === 'Y';
      if (!checkCamWoEntry) {
        this.pendingCamViolation = null;
        this.gradeAndWorktypeUpdate();
        return;
      }
      this.lineWOForm.patchValue({ ignoreCheckCamSpecAge: 'N' }, { emitEvent: false });
      const camReq = this.buildCamRequest({ ntr: this.lineWOForm.get('ntrId')?.value || null, mdot: mfgDot });
      this.lineMdotCamValidating = true;
      this.createWorkOrderService.getMatchingCamSpec(this.dealer.id, camReq).subscribe({
        next: (response) => {
          this.lineMdotCamValidating = false;
          // const treadVal = this.lineWOForm.get('treadId')?.value;
          // const treadId = typeof treadVal === 'object' ? treadVal?.id : treadVal;
          // const selectedTread = treadId ? this.treads.find((t) => t.id === treadId) : null;
          // const m3Flag = selectedTread?.m3Flag === 'Y';
          const violation = this.createWorkOrderService.checkV4MdotCamSpecs(
            {
              mfgDot: this.lineWOForm.get('mfgDot')?.value || '',
              application: this.lineWOForm.get('application')?.value || null,
              ignoreCheckCamSpecAge: this.lineWOForm.get('ignoreCheckCamSpecAge')?.value || ''
            },
            response,
            this.headerSettings!
          );
          if (violation?.ageInError) {
            // If an NTR violation was being shown but not yet resolved, preserve it so recheckCamFields() re-validates it on save.
            if (this.pendingCamViolation?.type === 'ntr') {
              this.camFixedViolationTypes.add('ntr');
            }
            this.pendingCamViolation = {
              type: 'age',
              value: String(violation.value ?? ''),
              limit: String(violation.limit ?? ''),
              action: violation.action ?? '',
              disposition: violation.disposition ?? '',
              treads: violation.treads ?? [],
              application: violation.application ?? null,
              axle: violation.axle ?? null
            };
            // Compute downgrade tread options: intersection of authorised treads (this.treads) and CAM spec tread list.
            // Mirrors LimitFailureManagedBean.getDownGradeTreadDisplay() AGE branch: finds treadDescBean by name
            // (case-insensitive) and checks existeInTheList(treadDescBean.getTreadDesc(), treadAuthorized).
            if (this.pendingCamViolation.action?.toUpperCase() === 'DOWNGRADE') {
              const specTreadsLower = (violation.treads ?? []).map((n) => n?.toLowerCase());
              this.camDowngradeTreads = this.treads.filter((t) => specTreadsLower.includes(t.value?.toLowerCase()));
            } else {
              this.camDowngradeTreads = [];
            }
            this.selectedDowngradeTread = this.camDowngradeTreads[0] ?? null;
            this.lineCamRejected = false;
            this.focusCamViolationBanner();
            // A new violation was found — cancel any deferred save; user must resolve it first.
            this.pendingSaveAction = null;
            if (
              this.lineScProcessCode &&
              violation.action?.toUpperCase() === 'REJECT' &&
              violation.disposition?.toUpperCase() === 'SCRAP'
            ) {
              this.createWorkOrderService.getDisposalChargeByDescription(this.dealer.id, violation.disposition!).subscribe({
                next: (dc) => this.lineWOForm.patchValue({ disposalCharge: dc ?? null }, { emitEvent: false }),
                error: () => this.lineWOForm.patchValue({ disposalCharge: null }, { emitEvent: false })
              });
            } else {
              this.lineWOForm.patchValue({ disposalCharge: null }, { emitEvent: false });
            }
          } else {
            // If an NTR violation was being shown but not yet resolved, preserve it so recheckCamFields() re-validates it on save.
            if (this.pendingCamViolation?.type === 'ntr') {
              this.camFixedViolationTypes.add('ntr');
            }
            this.pendingCamViolation = null;
            this.camDowngradeTreads = [];
            this.selectedDowngradeTread = null;
            this.gradeAndWorktypeUpdate();
            // MFG DOT passed — remove from pending rechecks, execute the deferred save if one is waiting.
            this.camFixedViolationTypes.delete('age');
            this.executePendingSaveIfReady();
          }
        },
        error: () => {
          this.lineMdotCamValidating = false;
          this.gradeAndWorktypeUpdate();
        }
      });
      return;
    }
    if (mfgDot || this.headerSettings?.isMfgDotFieldRequired) {
      const useWeeks = ['SA', 'NA'].includes(this.headerSettings?.zone?.toUpperCase() || '') || this.headerSettings?.adjCalMethod === '3';
      const hint = useWeeks
        ? this.translationService.translate('errMsgMfgDot2Text')
        : this.translationService.translate('errMsgMfgDot1Text');
      const existing = mfgDotCtrl?.errors ? { ...mfgDotCtrl.errors } : {};
      mfgDotCtrl?.setErrors({ ...existing, mfgDotFormat: { hint } });
      // If an NTR violation was being shown but not yet resolved, preserve it so recheckCamFields() re-validates it on save.
      if (this.pendingCamViolation?.type === 'ntr') {
        this.camFixedViolationTypes.add('ntr');
      }
      this.pendingCamViolation = null;
      return;
    }
    // If an NTR violation was being shown but not yet resolved, preserve it so recheckCamFields() re-validates it on save.
    if (this.pendingCamViolation?.type === 'ntr') {
      this.camFixedViolationTypes.add('ntr');
    }
    this.pendingCamViolation = null;
    if (mfgDotCtrl?.hasError('mfgDotFormat')) {
      const { ...rem } = mfgDotCtrl.errors as Record<string, any>;
      mfgDotCtrl.setErrors(Object.keys(rem).length ? rem : null);
    }
    this.gradeAndWorktypeUpdate();
  }

  /**
   * Client-side mirror of the Part4a backend guard:
   * when customerRtDot === 'R' (REQUIRED), the line is CAM-rejected, and NTR > 0,
   * RT DOT must not be blank. Sets/clears `rtDotRequiredForReject` on the rtDot control.
   */
  private checkRtDotRequiredForReject(): void {
    const rtDotCtrl = this.lineWOForm.get('rtDot');
    if (!rtDotCtrl) return;

    const shouldCheck = this.customerRtDot === 'R' && this.lineCamRejected;
    if (shouldCheck) {
      const ntrRaw = this.lineWOForm.get('ntrId')?.value;
      const ntrPositive = ntrRaw != null && ntrRaw !== '' && !isNaN(parseInt(String(ntrRaw), 10)) && parseInt(String(ntrRaw), 10) > 0;
      const rtDotBlank = !(rtDotCtrl.value || '').trim();
      if (ntrPositive && rtDotBlank) {
        const existing = rtDotCtrl.errors ? { ...rtDotCtrl.errors } : {};
        rtDotCtrl.setErrors({ ...existing, rtDotRequiredForReject: true });
        return;
      }
    }

    if (rtDotCtrl.hasError('rtDotRequiredForReject')) {
      const rest = { ...(rtDotCtrl.errors ?? {}) } as Record<string, any>;
      delete rest['rtDotRequiredForReject'];
      rtDotCtrl.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  /**
   * Real-time RT DOT validation — mirrors the V8 guard in createSingleWorkOrderLineV2.
   * Calls POST /validateRtDot with the current rtDot and mfgDot values and sets a
   * `rtDotInvalid` form error on the rtDot control when the server rejects the value.
   */
  private checkRtDotValidation(rtDot: string): void {
    const rtDotCtrl = this.lineWOForm.get('rtDot');
    if (!rtDotCtrl) return;

    // Clear any existing server-side error before re-validating
    if (rtDotCtrl.hasError('rtDotInvalid')) {
      const rest = { ...(rtDotCtrl.errors ?? {}) } as Record<string, any>;
      delete rest.rtDotInvalid;
      rtDotCtrl.setErrors(Object.keys(rest).length ? rest : null);
    }

    if (!rtDot || !this.dealer?.id) return;

    const mfgDot = (this.lineWOForm.get('mfgDot')?.value || '') as string;
    this.rtDotValidating = true;
    this.createWorkOrderService
      .validateRtDot(this.dealer.id, rtDot, mfgDot)
      .pipe(finalize(() => (this.rtDotValidating = false)))
      .subscribe({
        next: (res) => {
          if (!res.valid) {
            const existing = rtDotCtrl.errors ? { ...rtDotCtrl.errors } : {};
            rtDotCtrl.setErrors({ ...existing, rtDotInvalid: { message: this.rtDotErrorFormatter(res.errorMessage) } });
          }
        },
        error: () => {}
      });
  }

  private buildCamRequest(overrides: { ntr: number | null; mdot: string | null }): CamRequest {
    const makeVal = this.lineWOForm.get('makeId')?.value;
    const seriesVal = this.lineWOForm.get('seriesId')?.value;
    const sizeVal = this.lineWOForm.get('sizeId')?.value;
    const treadVal = this.lineWOForm.get('treadId')?.value;
    const prodCustVal = this.lineWOForm.get('productionCustomerId')?.value;
    const treadId = typeof treadVal === 'object' ? treadVal?.id : treadVal;
    const selectedTread = treadId ? this.treads.find((t) => t.id === treadId) : null;
    const m3Flag = selectedTread?.m3Flag === 'Y';
    return {
      make: (typeof makeVal === 'object' ? makeVal?.name : null) || null,
      series: (typeof seriesVal === 'object' ? seriesVal?.value : null) || null,
      size: (typeof sizeVal === 'object' ? sizeVal?.value : null) || null,
      tread: (typeof treadVal === 'object' ? treadVal?.value : null) || null,
      workType: this.lineWOForm.get('workType')?.value || '',
      application: this.lineWOForm.get('application')?.value?.value || null,
      axle: this.lineWOForm.get('axle')?.value?.value || null,
      customerId: this.extractId(prodCustVal) || '',
      customerCode: (typeof prodCustVal === 'object' ? prodCustVal?.code : null) || null,
      ntr: overrides.ntr,
      mdot: overrides.mdot,
      m3Flag,
      ignoreCheckCamSpecNtr: false,
      ignoreCheckCamSpecAge: false
    };
  }

  // -------- Line creation --------

  private createWorkOrderLineFromFormData(): WorkOrderLineInterface {
    const f = this.lineWOForm.value;
    const lineNumber = this.isEditMode && this.editingLine ? this.editingLine.lineNumber : this.getNextLineNumber().toString();
    const moldCtrl = this.lineWOForm.get('moldType');
    const moldTypeValue = this.showMoldRadio && moldCtrl?.enabled && moldCtrl.value ? moldCtrl.value : null;

    return {
      lineType: 'single',
      id: this.isEditMode && this.editingLine?.id ? this.editingLine.id : this.generateUniqueId(),
      lineNumber,
      barcode: f.barcode || '',
      casingLocation: f.casingLocation,
      workType: f.workType || 'RR',
      post: f.post || '',
      woSite: f.woSite,
      processCodeId: f.processCodeId,
      productionCustomerId: f.productionCustomerId,
      customerCode: f.customerCode || '',
      customerName: f.customerName || '',
      receivedDate: f.receivedDate,
      completeDate: f.completeDate,
      dueDate: f.dueDate,
      makeId: f.makeId,
      seriesId: f.seriesId,
      sizeId: f.sizeId,
      treadId: f.treadId,
      moldType: moldTypeValue,
      application:
        typeof f.application === 'object' && f.application !== null
          ? (f.application as ApplicationOption).value || (f.application as ApplicationOption).id
          : f.application || null,
      axle: typeof f.axle === 'object' && f.axle !== null ? (f.axle as AxleOption).value || (f.axle as AxleOption).id : f.axle || null,
      treadWidth: f.treadWidth,
      speedIndex: f.speedIndex,
      ntrId: this.headerSettings?.ntrFlag === 'N' || !this.headerSettings?.ntrFlag ? null : f.ntrId,
      mfgDot: f.mfgDot || '',
      rtDot: this.customerRtDot === 'N' ? null : f.rtDot,
      brandNumber: f.brandNumber || '',
      sequenceNumber: f.sequenceNumber,
      repair: f.repair,
      chamberNum: f.chamberNum,
      minCircumference: f.minCircumference,
      maxCircumference: f.maxCircumference,
      treadDepthMin: this.showTreadDepthFields ? f.treadDepthMin || null : null,
      treadDepthMax: this.showTreadDepthFields ? f.treadDepthMax || null : null,
      casingValue: f.casingValue,
      lengthId:
        this.headerSettings?.lengthFlag === 'N' || !this.headerSettings?.lengthFlag
          ? null
          : typeof f.lengthId === 'object' && f.lengthId !== null
          ? (f.lengthId as any).id
          : f.lengthId || null,
      ignoreCheckCamSpecNtr: f.ignoreCheckCamSpecNtr || null,
      ignoreCheckCamSpecAge: f.ignoreCheckCamSpecAge || null,
      casingNotes: f.casingNotes || '',
      camSpec: f.camSpec,
      systemGrade: f.systemGrade,
      overrideGrade: typeof f.overrideGrade === 'object' && f.overrideGrade !== null ? (f.overrideGrade as any).id : f.overrideGrade || '',
      wheelType: typeof f.wheelType === 'object' && f.wheelType !== null ? (f.wheelType as WheelOption).id : f.wheelType || '',
      wheelColor: typeof f.wheelColor === 'object' && f.wheelColor !== null ? (f.wheelColor as WheelOption).id : f.wheelColor || '',
      sendToServicePostFlag: f.sendToServicePostFlag || 'N',
      scrapCodeId: this.lineScProcessCode
        ? typeof f.scrapCode === 'object'
          ? (f.scrapCode as any)?.id || null
          : f.scrapCode || null
        : null,
      npCodeId: this.lineNpProcessCode ? (typeof f.npCode === 'object' ? (f.npCode as any)?.id || null : f.npCode || null) : null,
      disposalChargeId:
        this.lineScProcessCode || this.lineCamRejected
          ? typeof f.disposalCharge === 'object'
            ? (f.disposalCharge as any)?.id || null
            : null
          : null,
      // CAM reject: mark line as rejected at WO entry — backend applies the three-path legacy logic
      // via RejectCasingService (COMPLETE / REJECT_FOR_ADMIN / REJECT_PENDING based on plant/customer config)
      rejectInd: this.lineCamRejected ? 'Y' : null,
      camViolationField: this.lineCamViolationField,
      camViolationDisposition: this.lineCamViolationDisposition,
      casingStatus: this.lineCamRejected ? 'COMPLETE' : f.casingStatus
    } as WorkOrderLineInterface;
  }

  // -------- Edit mode patching --------

  private patchFormForEdit(item: WorkOrderLineInterface): void {
    const makeId = this.extractId(item.makeId);
    const seriesId = this.extractId(item.seriesId);
    const sizeId = this.extractId(item.sizeId);
    const plantId = this.plant?.id;
    const processCodeId = this.extractId(item.processCodeId);
    const processCodeObj = this.lineProcessCodes.find((p) => p.id === processCodeId) ?? null;
    const prodCustId = this.extractId(item.productionCustomerId);
    const prodCustObj = this.productionCustomers.find((c) => c.id === prodCustId) ?? null;
    const editWarnings: string[] = [];
    if (processCodeId && !processCodeObj) {
      editWarnings.push('The process code from this line is not available for the current plant. Please select a valid process code.');
    }
    if (prodCustId && !prodCustObj) {
      editWarnings.push('The production customer from this line is no longer available. Please select a valid production customer.');
    }
    if (editWarnings.length > 0) {
      this.pendingCamViolation = null; // avoid dual-banner confusion
      this.lineErrors = editWarnings;
    }
    const makeObj = this.makes.find((m) => m.id === makeId) || item.makeId || '';
    // Map overrideGrade, wheelType and wheelColor to option objects when possible so
    // the dropdowns show the correct selected item when opening in edit mode.
    const overrideGradeId = this.extractId(item.overrideGrade);
    const overrideGradeObj = this.casingGradeOptions.find((g) => g.id === overrideGradeId) || item.overrideGrade || '';
    const wheelTypeId = this.extractId(item.wheelType);
    const wheelTypeObj = this.wheelOptions.find((w) => w.id === wheelTypeId) || item.wheelType || '';
    const wheelColorId = this.extractId(item.wheelColor);
    const wheelColorObj = this.wheelColorOptions.find((c) => c.id === wheelColorId) || item.wheelColor || '';
    // Map scrap/np codes to reject reason option objects so the selects show saved values on edit
    const scrapId = this.extractId(item.scrapCodeId);
    const scrapObj = this.rejectReasonOptions.find((r) => r.id === scrapId) || item.scrapCodeId || '';
    const npId = this.extractId(item.npCodeId);
    const npObj = this.rejectReasonOptions.find((r) => r.id === npId) || item.npCodeId || '';
    // Map lengthId to LengthOption so the length dropdown shows correctly when editing.
    const lengthIdVal = this.extractId(item.lengthId);
    const lengthObj = this.lengthOptions.find((l) => l.id === lengthIdVal) || item.lengthId || '';

    this.lineWOForm.patchValue(
      {
        barcode: item.barcode || '',
        casingLocation: item.casingLocation || '',
        casingStatus: item.casingStatus || '',
        workType: item.workType || '',
        post: item.post || '',
        woSite: item.woSite || '',
        processCodeId: processCodeObj ?? '',
        productionCustomerId: prodCustObj ?? '',
        customerCode: item.customerCode || '',
        customerName: item.customerName || '',
        makeId: makeObj,
        seriesId: item.seriesId || '',
        sizeId: item.sizeId || '',
        treadId: item.treadId || '',
        application: this.applicationOptions.find((a) => a.value === item.application) || item.application || '',
        axle: this.axleOptions.find((a) => a.value === item.axle) || item.axle || '',
        treadWidth: item.treadWidth || '',
        speedIndex: item.speedIndex || '',
        ntrId: item.ntrId || '',
        mfgDot: item.mfgDot || '',
        rtDot: item.rtDot || '',
        brandNumber: item.brandNumber || '',
        sequenceNumber: item.sequenceNumber || '',
        repair: item.repair || '',
        chamberNum: item.chamberNum || '',
        minCircumference: item.minCircumference || '',
        maxCircumference: item.maxCircumference || '',
        moldType: item.moldType || '',
        treadDepthMin: item.treadDepthMin || '',
        treadDepthMax: item.treadDepthMax || '',
        casingValue: item.casingValue != null ? this.applyFormatNumber(String(item.casingValue)) : '',
        casingNotes: item.casingNotes || '',
        camSpec: item.camSpec || '',
        systemGrade: item.systemGrade || '',
        lengthId: lengthObj,
        overrideGrade: overrideGradeObj,
        wheelType: wheelTypeObj,
        wheelColor: wheelColorObj,
        scrapCode: scrapObj,
        npCode: npObj,
        ignoreCheckCamSpecNtr: item.ignoreCheckCamSpecNtr || '',
        ignoreCheckCamSpecAge: item.ignoreCheckCamSpecAge || ''
      },
      { emitEvent: false }
    );

    const editCode = typeof processCodeObj === 'object' ? (processCodeObj as any)?.code || '' : (processCodeObj as string) || '';
    this.updateCasingValueVisibility(String(editCode).toUpperCase());

    // Manually apply the same logic that runs in the processCodeId valueChanges handler
    // because patchValue was called with { emitEvent: false }.
    const upper = String(editCode).toUpperCase();
    const specialCodes = ['IO', 'SC', 'NP', 'RO', 'SO', 'RS', 'DT'];
    const isSpecial = specialCodes.includes(upper);

    this.lineScProcessCode = upper === 'SC';
    this.lineNpProcessCode = upper === 'NP';

    // Scrap / NP validators and visibility
    const scrapCtrl = this.lineWOForm.get('scrapCode');
    const npCtrl = this.lineWOForm.get('npCode');
    if (this.lineScProcessCode) {
      scrapCtrl?.setValidators([Validators.required]);
    } else {
      scrapCtrl?.clearValidators();
      scrapCtrl?.setValue('', { emitEvent: false });
    }
    scrapCtrl?.updateValueAndValidity({ emitEvent: false });

    if (this.lineNpProcessCode) {
      npCtrl?.setValidators([Validators.required]);
    } else {
      npCtrl?.clearValidators();
      npCtrl?.setValue('', { emitEvent: false });
    }
    npCtrl?.updateValueAndValidity({ emitEvent: false });

    // Disposal charge lookup when SC
    if (this.lineScProcessCode && this.customerDisposalChargesFk) {
      this.createWorkOrderService.getDisposalChargeById(this.dealer.id, this.customerDisposalChargesFk).subscribe({
        next: (dc) => this.lineWOForm.patchValue({ disposalCharge: dc ?? null }, { emitEvent: false }),
        error: () => this.lineWOForm.patchValue({ disposalCharge: null }, { emitEvent: false })
      });
    } else {
      this.lineWOForm.patchValue({ disposalCharge: null }, { emitEvent: false });
    }

    // Special codes disable certain controls
    const treadCtrl = this.lineWOForm.get('treadId');
    const appCtrl = this.lineWOForm.get('application');
    const axleCtrl = this.lineWOForm.get('axle');
    if (isSpecial) {
      treadCtrl?.setValue('', { emitEvent: false });
      treadCtrl?.disable({ emitEvent: false });
      appCtrl?.setValue('', { emitEvent: false });
      appCtrl?.disable({ emitEvent: false });
      axleCtrl?.setValue('', { emitEvent: false });
      axleCtrl?.disable({ emitEvent: false });
    } else {
      treadCtrl?.enable({ emitEvent: false });
      appCtrl?.enable({ emitEvent: false });
      axleCtrl?.enable({ emitEvent: false });
    }

    // Ensure tread validator reflects current moldType/edit state (custom mold 'M' => optional)
    this.updateTreadRequiredBasedOnMold();

    this.updateWOLNTR();

    if (makeId && this.dealer?.id) {
      this.seriesLoading = true;
      this.createWorkOrderService.getSeries(this.dealer.id, makeId).subscribe({
        next: (d) => {
          this.series = d;
          this.seriesLoading = false;
          const obj = d.find((s) => s.id === seriesId);
          if (obj) this.lineWOForm.patchValue({ seriesId: obj }, { emitEvent: false });
        },
        error: () => {
          this.seriesLoading = false;
        }
      });
      if (seriesId) {
        this.sizesLoading = true;
        this.createWorkOrderService.getSizes(this.dealer.id, makeId, seriesId, plantId).subscribe({
          next: (d) => {
            this.sizes = d;
            this.sizesLoading = false;
            const obj = d.find((s) => s.id === sizeId);
            if (obj) this.lineWOForm.patchValue({ sizeId: obj }, { emitEvent: false });
          },
          error: () => {
            this.sizesLoading = false;
          }
        });
        if (sizeId && plantId && isSpecial) {
          this.treadsLoading = true;
          this.createWorkOrderService
            .getTreads(this.dealer.id, sizeId, plantId, undefined, makeId || undefined, seriesId || undefined)
            .subscribe({
              next: (d) => {
                this.treads = d;
                this.treadsLoading = false;
                const treadId = this.extractId(item.treadId);
                const obj = d.find((t) => t.id === treadId);
                if (obj) this.lineWOForm.patchValue({ treadId: obj }, { emitEvent: false });
              },
              error: () => {
                this.treadsLoading = false;
              }
            });
        }
      }
    }
  }

  // -------- Validators --------

  private lineGroupValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const fg = group as FormGroup;
      const min = parseFloat(fg.get('minCircumference')?.value);
      const max = parseFloat(fg.get('maxCircumference')?.value);
      if (!isNaN(min) && !isNaN(max) && min > max) {
        return { circumferenceRange: 'Min circumference must be less than or equal to max circumference' };
      }
      return null;
    };
  }

  // -------- Utilities --------

  private generateUniqueId(): string {
    return 'wo-line-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  private extractId(value: any): string {
    if (!value) return '';
    if (typeof value === 'object' && value.id) return value.id;
    return typeof value === 'string' ? value : '';
  }

  public rtDotErrorFormatter(msg: string): string {
    if (msg.includes('Factory Code')) {
      return this.translationService.translate('errMsgRtDot3Text');
    } else if (msg.includes('01 and 54')) {
      return this.translationService.translate('errMsgRtDot2Text');
    } else if (msg.includes('01 and 12')) {
      return this.translationService.translate('errMsgRtDot1Text');
    }
  }
}
